import React, { useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { Search } from "lucide-react";
import { UnitSearchResult } from "./types";
import { Input, SearchEmpty, SearchResultMeta, SearchResultRow, SearchResults, SearchResultSerial, SearchWrap } from "./styles";

interface SerialSearchProps {
    result: UnitSearchResult;
    onSearch: (q: string) => void;
    onSelect: (serial: string) => void;
}

export default function AssetTagSearch({ result, onSearch, onSelect }: SerialSearchProps) {

    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);

    // onSearch is a fresh closure every render (useUnits doesn't memoize its
    // functions), so read the latest one through a ref rather than rebuilding
    // (and resetting) the debounce timer on every keystroke-triggered re-render.
    const onSearchRef = useRef(onSearch);
    onSearchRef.current = onSearch;

    const debouncedRef = useRef(debounce((q: string) => onSearchRef.current(q), 300));
    useEffect(() => () => debouncedRef.current.cancel(), []);

    const handleChange = (value: string) => {
        setQuery(value);
        setOpen(true);
        debouncedRef.current(value);
    };

    const handleSelect = (serial: string) => {
        onSelect(serial);
        setOpen(false);
        setQuery("");
        onSearch("");
    };

    return (
        <SearchWrap>
            <Input
                value={query}
                onChange={(e) => handleChange(e.target.value)}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                placeholder="Find asset tag..."
            />
            {open && query.trim() && (
                <SearchResults>
                    {!result ? (
                        <SearchEmpty>No matches.</SearchEmpty>
                    ) : (
                        <SearchResultRow key={result.id} onMouseDown={() => handleSelect(result.serial)}>
                            <SearchResultSerial>
                                <Search size={12} style={{ marginRight: 4 }} />
                                {result.serial}
                            </SearchResultSerial>
                            {
                                result.scope === 'full' ? (
                                    <SearchResultMeta>
                                        {result.itemName} · {result.status}
                                        {result.holderId ? ` · ${result.holderId}` : ""}
                                    </SearchResultMeta>
                                ) : (
                                    <SearchResultMeta>
                                        {result.itemName} · {result.unit}
                                    </SearchResultMeta>
                                )
                            }
                        </SearchResultRow>
                    )}
                </SearchResults>
            )}
        </SearchWrap>
    );
}
